// The season switch and the time dock: date chips, date picker, hour slider with the sun curve, time-lapse button.
// Controls: season.switch, time.dates, time.pickDate, time.hour, time.play. A press calls the map's own functions
// (api.setTime, scrubTime, setSeason), which move the sky as before and report the new value to the runtime.
import { html } from '../dom.js';
import { sunPosition, localToUTC } from '../sun.js';
import { icon } from './icons.js';
import { monthDay, parseMonthDay } from './layout.js';

export const SEASON_ICON = { winter: 'snow', autumn: 'leaf', summer: 'sun', spring: 'sprout' };

export function timeParts(c) {
  const { api, T, tx, i18n } = c;
  const $ = (sel) => document.querySelector(sel);
  const year = api.resort.site.year || new Date().getFullYear();
  const all = () => api.resort.ui?.seasons || []; // ui.seasons: { id, label, date: "m-d", ready }
  const offered = () => c.v('season.offered') || [];
  // the switch lists the seasons this page can show, and the package's unfinished ones with a "Soon" mark
  const seasons = () => all().filter((s) => offered().includes(s.id) || !s.ready).map((s) => ({ ...s, ready: offered().includes(s.id), name: c.seasonName(s.id) }));
  const dates = () => c.v('time.dates') || []; // { m, d, label, note, season }: the dock shows the chosen season's
  const dateSeason = (d) => d.season || all()[0]?.id;
  let shownSeason = all().some((s) => s.id === c.season) ? c.season : all()[0]?.id;
  let playing = false, sun = null; // the time-lapse runs; the sun as last reported (setSun)

  function daylight(m, d) { // hours of sun above the horizon (refraction-corrected), for the custom chip's note
    let n = 0;
    for (let i = 0; i < 96; i++) if (sunPosition(localToUTC(year, m, d, (i + 0.5) / 4)).alt > -0.83) n++;
    return n === 0 ? T('time.polarNight') : n === 96 ? T('time.midnightSun') : T('time.daylight', { n: i18n.num(Math.round(n / 4)) });
  }
  function drawCurve() {
    const s = api.getState();
    let d = '';
    for (let i = 0; i <= 96; i++) {
      const h = (i / 96) * 24, alt = sunPosition(localToUTC(year, s.month, s.day, h)).alt;
      d += `${i ? 'L' : 'M'}${(h / 24) * 240} ${30 - alt * 1.1}`;
    }
    $('.sunline').setAttribute('d', d);
  }
  function sync() {
    const s = api.getState(), D = dates();
    const vis = (d) => !shownSeason || dateSeason(d) === shownSeason;
    const idx = c.has('time.dates') ? D.findIndex((d) => vis(d) && d.m === s.month && d.d === s.day) : -1;
    document.querySelectorAll('.date[data-i]').forEach((b, i) => {
      b.hidden = !vis(D[i]);
      b.classList.toggle('on', i === idx); b.setAttribute('aria-checked', String(i === idx));
    });
    const custom = $('#date-custom');
    if (custom) {
      custom.classList.toggle('on', idx < 0); custom.setAttribute('aria-checked', String(idx < 0));
      custom.querySelector('b span').textContent = idx < 0 ? T('time.day', i18n.dayVars(s.month, s.day)) : T('time.pick');
      custom.querySelector('small').textContent = idx < 0 ? daylight(s.month, s.day) : T('time.pick.note');
      $('#date-pick').value = `${year}-${monthDay(s.month, s.day)}`;
    }
    document.querySelectorAll('.season').forEach((b) => { const on = b.dataset.season === shownSeason; b.classList.toggle('on', on); b.setAttribute('aria-checked', String(on)); });
    const hour = $('#hour');
    if (hour) { hour.value = s.hour; drawCurve(); }
    if (sun) setSun(...sun); // a dock drawn again shows the clock and the sun at once
  }
  function setSun(pos, id) { // id: the phase of the day (sun.js phase()), named by the interface key phase.<id>
    sun = [pos, id];
    c.phase = id; // (VIS-230: a phone's Light choices say it under their clock)
    const h = api.getState().hour, phase = $('#phase'), dot = $('.sundot');
    if (phase) {
      phase.textContent = T('time.phase', { phase: T(`phase.${id}`), alt: `${pos.alt >= 0 ? '+' : '−'}${i18n.num(Math.abs(pos.alt), 1)}` });
      $('#clock').textContent = i18n.clock(h);
    }
    if (dot) { dot.setAttribute('cx', (h / 24) * 240); dot.setAttribute('cy', 30 - pos.alt * 1.1); }
    document.body.classList.toggle('dark', pos.alt < -6);
  }
  const setDate = (m, d) => { api.setTime({ month: m, day: d }, true); sync(); }; // WS-4: the sun glides to the new date

  const parts = {
    'season.switch': {
      can: () => seasons().length > 0,
      html: () => html`<div class="seasons" role="radiogroup" aria-label="${T('season.label')}">
        ${seasons().map((s) => html`<button class="season" role="radio" data-season="${s.id}" title="${s.ready ? s.name : T('season.soonTitle', { season: s.name })}">${icon(SEASON_ICON[s.id] || 'sun')}<span>${s.name}</span>${s.ready ? '' : html`<i class="soon">${T('season.soon')}</i>`}</button>`)}
      </div>`,
      // A season this page offers switches the scene and moves the light to the season's date: live (WS-41), or by
      // reloading the map in that season (?season=, hour and camera kept). An unfinished one only moves the light
      // and says so.
      wire: (root) => root.querySelectorAll('.season').forEach((b) => (b.onclick = () => {
        const s = seasons().find((x) => x.id === b.dataset.season), md = parseMonthDay(s.date);
        // live: also the season on the bar (c.season moves only once a switch has drawn): a press during a switch is
        // queued by setSeason with its own date, not left as a date change under the season being switched to
        if (s.ready && api.setSeason) {
          if (md) api.setTime({ month: md[0], day: md[1] }, true);
          api.setSeason(s.id);
          return;
        }
        if (s.ready && s.id !== c.season) {
          const q = new URLSearchParams(location.search);
          q.set('season', s.id); if (md) q.set('date', `${md[0]}-${md[1]}`);
          q.set('hour', String(+api.getState().hour.toFixed(2))); if (api.viewParam) q.set('cam', api.viewParam()); // WS-35: keep the hour and the camera
          location.search = `?${q}`;
          return;
        }
        shownSeason = s.id;
        if (md) setDate(...md); else sync();
        if (!s.ready) c.toast(T('season.toast'), T('season.comingSoon', { season: s.name }), T('season.lightOnly', i18n.dayVars(...(md || [1, 1]))));
      })),
    },
    'time.dates': {
      can: () => dates().length > 0,
      html: () => html`${dates().map((d, i) => html`<button class="date" data-i="${i}" role="radio"><b>${i18n.named(d.label, T('time.day', i18n.dayVars(d.m, d.d)))}</b><small>${tx(d.note)}</small></button>`)}`,
      wire: (root) => root.querySelector('.dates').addEventListener('click', (e) => {
        const b = e.target.closest('.date[data-i]'); if (!b) return;
        const d = dates()[Number(b.dataset.i)];
        setDate(d.m, d.d);
      }),
    },
    'time.pickDate': {
      html: () => html`<button class="date custom" id="date-custom" role="radio" title="${T('time.pick.title')}"><b>${icon('calendar')}<span>${T('time.pick')}</span></b><small>${T('time.pick.note')}</small>
        <input type="date" id="date-pick" tabindex="-1" aria-hidden="true" min="${year}-01-01" max="${year}-12-31"></button><!-- round 6: custom date -->`,
      wire(root) {
        const custom = root.querySelector('#date-custom'), pick = root.querySelector('#date-pick');
        custom.addEventListener('click', (e) => {
          if (e.target === pick) return;
          try { pick.showPicker(); } catch { pick.focus(); pick.click(); } // errors:ignore: older browsers have no showPicker()
        });
        pick.addEventListener('change', () => {
          const [, m, d] = pick.value.split('-').map(Number);
          if (m && d) setDate(m, d);
        });
      },
    },
    'time.hour': {
      html: () => html`<div class="timeline">
        <svg class="curve" viewBox="0 0 240 48" preserveAspectRatio="none"><path class="horizon" d="M0 30H240"/><path class="sunline" d=""/><circle class="sundot" r="3.2" cx="0" cy="0"/></svg>
        <input id="hour" type="range" min="0" max="24" step="0.05" aria-label="${T('time.hour')}">
        <div class="ticks"><span>00</span><span>06</span><span>12</span><span>18</span><span>24</span></div>
      </div>`,
      wire(root) {
        const hour = root.querySelector('#hour');
        // Round 6 scrub: the slider only sets a target hour; main.js eases the sun there once per frame at full frame rate.
        hour.addEventListener('input', () => api.scrubTime(Number(hour.value)));
        hour.addEventListener('pointerdown', () => { if (playing) $('#play')?.click(); });
      },
    },
    'time.play': {
      // VIS-128: a toggle from the start: its name stays (aria-label), the pressed state and the tooltip say what it does now
      html: () => html`<button class="round-btn small" id="play" aria-label="${T('time.play')}" title="${T(playing ? 'time.pause' : 'time.play')}" aria-pressed="${String(playing)}">${icon(playing ? 'pause' : 'play')}</button>`,
      wire(root) {
        const play = root.querySelector('#play');
        play.onclick = () => { playing = !playing; play.innerHTML = icon(playing ? 'pause' : 'play'); play.setAttribute('aria-pressed', String(playing)); play.title = T(playing ? 'time.pause' : 'time.play'); };
      },
    },
  };

  const dock = {
    slot: 'dock',
    html(kids) {
      if (!kids.length) return '';
      const k = Object.fromEntries(kids.map((x) => [x.id, x.html]));
      const chips = k['time.dates'] || k['time.pickDate'];
      return html`<div id="dock" class="shell${chips ? '' : ' slim'}">
        <div class="core">
          ${chips ? html`<div class="dates" role="radiogroup" aria-label="${T('time.date')}">
            ${k['time.dates'] || ''}
            ${k['time.pickDate'] || ''}
          </div>` : ''}
          ${k['time.hour'] || ''}
          ${k['time.hour'] || k['time.play'] ? html`<div class="readout">
            ${k['time.play'] || ''}
            ${k['time.hour'] ? html`<div><strong id="clock">${i18n.clock(12)}</strong><small id="phase">—</small></div>` : ''}
          </div>` : ''}
        </div>
      </div>`;
    },
  };

  return {
    parts, dock, sync,
    isPlaying: () => playing,
    /** The season on screen changed (main.js setSeason, or the runtime). */
    setSeason(id) { c.season = id; if (all().some((x) => x.id === id)) shownSeason = id; },
    /** A draft season says so once the map is up. */
    start() {
      const draft = all().find((x) => x.id === c.season && !x.ready);
      if (draft) setTimeout(() => c.toast(T('season.toast'), T('season.draft', { season: c.seasonName(draft.id) }), T('season.draftNote')), 2500);
    },
    setSun,
    step(dt) {
      if (!c.has('time.play')) playing = false; // a hidden control does nothing
      if (!playing) return;
      const h = (api.getState().hour + dt * 1.5) % 24, hour = $('#hour');
      api.setTime({ hour: h });
      if (hour) hour.value = h;
    },
  };
}
